import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Stethoscope,
  Activity,
  LineChart,
  Sparkles,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import { NewsTicker } from "@/knowmeno/news/NewsTicker";

export const Route = createFileRoute("/medical-reset")({
  head: () => ({
    meta: [
      {
        title: "Medical Reset — Menopause care reimagined | KnowMeno",
      },
      {
        name: "description",
        content:
          "Symptom tracking + a doctor-grade Symptom Intelligence Report — with wearable sync coming soon.",
      },
      {
        property: "og:title",
        content: "Medical Reset — The new way to do menopause care",
      },
      {
        property: "og:description",
        content:
          "Symptom Intelligence Report + specialist matching — wearable sync coming soon.",
      },
      { name: "robots", content: "index, follow" },
    ],
  }),
  notFoundComponent: () => <div className="p-8">Not found.</div>,
  errorComponent: () => <div className="p-8">Something went wrong.</div>,
  component: MedicalResetPage,
});

function MedicalResetPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            ← Back to KnowMeno
          </Link>
          <div className="flex items-center gap-2 font-serif text-xl">
            <Stethoscope className="h-5 w-5" /> Medical Reset
          </div>
        </div>
      </header>

      <NewsTicker />

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 pt-12 pb-10 sm:pt-20">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
            <Sparkles className="h-3 w-3" /> New
          </span>
          <h1 className="mt-4 font-serif text-4xl leading-tight sm:text-5xl">
            Treat the <em>stage</em>, not just the symptom.
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Medical Reset combines daily symptom tracking with a doctor-grade
            Symptom Intelligence Report — and soon, wearable data will help the
            next provider you see understand your biometric markers alongside
            the stage of menopause you're in.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/app"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Start tracking <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/store"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold hover:bg-muted"
            >
              Shop wearables
            </Link>
          </div>
        </div>
      </section>

      {/* Three pillars */}
      <section className="mx-auto max-w-6xl px-4 py-10">
        <div className="grid gap-6 sm:grid-cols-3">
          <Pillar
            icon={<Activity className="h-6 w-6" />}
            title="Latest wearable tech"
            body="Wearable sync is coming soon — Oura, Fitbit, Garmin, Whoop, and Withings first, with Apple Watch to follow in our mobile app. HRV, sleep stages, resting heart rate, and temperature will flow straight into your picture."
            cta={{ to: "/store", label: "Browse devices" }}
          />
          <Pillar
            icon={<LineChart className="h-6 w-6" />}
            title="Symptom Intelligence Report"
            body="A clinical-grade PDF your doctor will actually read: 90-day symptom heatmap, biometric overlay, trigger correlations, HRT timeline and lab snapshot."
            cta={{ to: "/app", label: "See the report" }}
            highlight
          />
          <Pillar
            icon={<Stethoscope className="h-6 w-6" />}
            title="Specialist matching"
            body="Coming soon: geo-tagged menopause specialists who read your report before your visit, so you can stop re-explaining your story."
          />
        </div>
      </section>

      {/* Symptom Intelligence Report deep-dive */}
      <section className="border-y bg-muted/30">
        <div className="mx-auto max-w-6xl px-4 py-14">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                The centerpiece
              </span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl">
                Your Symptom Intelligence Report
              </h2>
              <p className="mt-4 text-muted-foreground">
                Most providers get 7 minutes with you. This is what we hand them
                first — so those minutes are about your treatment plan, not your
                backstory.
              </p>
              <ul className="mt-6 space-y-3 text-sm">
                <ReportRow label="Stage marker" body="Peri / meno / post with confidence score, FSH trend and cycle regularity." />
                <ReportRow label="Symptom heatmap" body="Last 90 days of hot flashes, sleep, mood, brain fog and joint pain — frequency × severity." />
                <ReportRow label="Biometric overlay" body="HRV, resting heart rate, sleep stages, and skin temperature — once you connect a wearable, coming soon." />
                <ReportRow label="Trigger correlations" body="Top triggers ranked by lift — alcohol → hot flash, late caffeine → broken sleep." />
                <ReportRow label="HRT timeline" body="Dose, adherence and response markers, with effective-date flags when your regimen changes." />
                <ReportRow label="Lab snapshot" body="Latest values from uploaded panels with reference ranges in context." />
              </ul>
              <p className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-4 w-4" /> Not medical advice. Generated
                for you to share with a licensed provider.
              </p>
            </div>

            <div className="rounded-lg border bg-card p-6 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Why your doctor will actually read it
              </div>
              <ol className="mt-4 space-y-3 text-sm">
                <li><strong>1.</strong> One PDF, structured the way a clinician scans charts.</li>
                <li><strong>2.</strong> Biometric markers — objective data, not just self-report — once you connect a wearable, coming soon.</li>
                <li><strong>3.</strong> Pattern context: what changed, when, and what correlated.</li>
                <li><strong>4.</strong> Frozen snapshot with a date — never drifts after you share it.</li>
              </ol>
              <Link
                to="/app"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              >
                Generate yours <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* News */}
      <section className="mx-auto max-w-6xl px-4 py-14">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-serif text-3xl">What's new in menopause</h2>
            <p className="mt-2 text-muted-foreground">
              We scan trusted medical sources — Menopause.org, NAMS, NIH, Mayo,
              BMJ — and surface what matters.
            </p>
          </div>
          <Link to="/news" className="text-sm font-semibold text-primary hover:underline">
            See all →
          </Link>
        </div>
        <div className="mt-6">
          <NewsTicker />
        </div>
      </section>

      <footer className="border-t">
        <div className="mx-auto max-w-6xl px-4 py-8 text-xs text-muted-foreground">
          KnowMeno is a wellness tracking tool, not a medical device. Always work
          with a qualified provider for diagnosis and treatment.
        </div>
      </footer>
    </div>
  );
}

function Pillar({
  icon,
  title,
  body,
  cta,
  highlight,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  cta?: { to: string; label: string };
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-6 ${
        highlight ? "border-primary bg-primary/5" : "bg-card"
      }`}
    >
      <div className="text-primary">{icon}</div>
      <h3 className="mt-3 font-serif text-lg">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
      {cta && (
        <Link
          to={cta.to}
          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
        >
          {cta.label} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}

function ReportRow({ label, body }: { label: string; body: string }) {
  return (
    <li className="flex gap-3">
      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
      <span>
        <strong>{label}.</strong> <span className="text-muted-foreground">{body}</span>
      </span>
    </li>
  );
}
